import type { OptBean } from "../../modules/s25-opt/s25.opt.component";
import type { OptUtilI } from "../../modules/s25-opt/s25.opt.util";
import type { Table } from "../../modules/s25-table/Table";
import type {
    ListCellValue,
    ListColumn,
    ListDateFormats,
    ListResponse,
    ListRowMapperOptions,
    ListTableColumnDefaultsOptions,
} from "./list.table.types";
import type { ListType } from "./list.with.options.component";

export interface ListConfig {
    caption: string;
    resultCountSuffix: string;
    hostId?: string;
    errorMessageLabel: string;
    listData: {
        compSubject: string;
        compType?: string;
        listColumnPrefName?: string;
        defaultSort: string;
        defaultOrder: "asc" | "desc";
    };
    loadOptBean: (refreshTable: () => void, runTime: Record<string, unknown>) => Promise<OptUtilI>;
    optBeanRunTime: (listType: ListType) => Record<string, unknown>;
    listStateId?: (listType: ListType) => string | undefined;
    fetchListData: (context: ListConfig.FetchContext) => Promise<ListResponse>;
    mergeColumns?: (cols: ListColumn[], listStateId: string | undefined) => ListColumn[];
    columnChooser?: {
        listColumnPrefName?: string;
    };
    formatRowCount: (counts: Table.RowCounts) => string;
    cellMapping?: ListConfig.CellMapping;
}

export namespace ListConfig {
    export type CellMappingContext = {
        refreshTable: () => void;
        getColumnId: (column: ListColumn) => string;
    };

    export type RowMapperOptions = ListRowMapperOptions<{
        contextId?: string | number;
        row: readonly unknown[];
    }>;

    export type CellMapping = {
        applyTableColumnDefaults?: (
            context: CellMappingContext,
            tableColumn: Table.Column,
            column: ListColumn,
            options: ListTableColumnDefaultsOptions,
        ) => void;
        mapCell?: (
            context: CellMappingContext,
            column: ListColumn,
            data: ListCellValue,
            formats: ListDateFormats,
        ) => [string, Table.Cell] | undefined;
        rowMapperOptions?: RowMapperOptions;
    };

    export type FetchContext = {
        config: ListConfig;
        listType: ListType;
        optBean: OptBean;
        tableQuery: Table.PaginatedQuery;
        abortSignal?: AbortSignal;
    };

    export function defaultFormatRowCount(suffix: string): (counts: Table.RowCounts) => string {
        return (counts) => `${counts.total} Matching ${suffix}`;
    }

    export function extractTableSort(tableQuery: Table.PaginatedQuery): { sortColumn: string; sortOrder: string } {
        if (!tableQuery.sortColumn.id) {
            return { sortColumn: "", sortOrder: "" };
        }

        return {
            sortColumn: String(tableQuery.sortColumn.id),
            sortOrder: tableQuery.sortColumn.order ?? "",
        };
    }
}
